import React from 'react';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { getVisionLabelForValue } from './ConstVisionBtns';

const VISUAL_WRAPUP_HELP = `
  <div>
    <p><strong>Resources: </strong>For more about AT accommodations for vision issues, see the following:
      <ul>
        <li><a href="https://www.pattan.net/Publications/Matching-AT-Features-to-Low-Vision-Needs" target="_blank">Matching Assistive Technology Features to Low Vision Needs</a> - a free download from PaTTAN (Pennsylvania Training and Technical Assistance Network). </li>
        <li><a href="https://www.wati.org/free-publications/assessing-students-needs-for-assistive-technology/page/2/" target="_blank">AT for Blind / Low Vision</a> - a free download from WATI (Wisconsin AT Initiative). School-focused.</li>
        <li>Search on "assistive technology for vision impairments" or include more specific issue such as "CVI"</li>
        <li>Ask a question on the <a href="https://qiat.org/" target="_blank">QIAT</a> listserv (a group of several thousand AT professionals)</li>        
      </ul>
    </p>
    <p><strong>Note: </strong>reminder that if visual issues seem particularly complex with this individual, consider referral to a vision specialist.</p>
  </div>
`
const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  wearsGlasses: PropTypes.string,
  betterWithGlasses: PropTypes.string,
  hasOtherVisualAids: PropTypes.string,
  visionIssues: PropTypes.arrayOf(PropTypes.string),
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  wearsGlasses: '',
  betterWithGlasses: '',
  hasOtherVisualAids: '',
  visionIssues: [],
};

function anyVisualConditions(wearsGlasses, betterWithGlasses, hasOtherVisualAids, visionIssues) {
  if (((betterWithGlasses==='true') && (wearsGlasses==='true'))
    || (hasOtherVisualAids==='true')
    || (visionIssues.length > 0)
  ){
    console.log("true anyVisualConditions");
    return(true);
  }
  else return(false);
}

function WrapupConditions(props){
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visionIssues = props.visionIssues;

  const exampleIssue = (visionIssues.length > 0) ? getVisionLabelForValue(visionIssues[0]).toLowerCase() : "";

  console.log("WrapupConditions, wg: " + wearsGlasses + " bwg: " + betterWithGlasses);
  return(
  <>
  <p>Based on your responses, here are a few considerations to keep in mind as you work with {firstName}:</p>
  <ul>
    {((betterWithGlasses==='true') && (wearsGlasses==='true')) ? 
      <li>
        {pronouns[2]} glasses help {pronouns[1]} see better, so make sure they're on.
      </li> 
    : <span></span>}
    {(hasOtherVisualAids==='true') ? 
      <li>
        Try to have {firstName}'s other visual aids and strategies available during this session, where applicable.
      </li> 
    : <span></span>}      
    {(visionIssues.length > 0) ? 
      <li>
        You reported that {firstName} may have some vision issues, including {exampleIssue} 
        {/* {visionIssues.map(function(issue, index) {return(<span key={index}>{issue}{', '}</span>);})} */}
        <ul>
          <li>This may affect {pronouns[2]} ability to visually engage with or attend to information presented on the computer screen</li>
          <li>There may be ways to accommodate this -- consider consulting with someone with specific training in this area.</li>
        </ul>
      </li> 
    : <span></span>}
  </ul>
  <p>This is a great start!</p>
  </>
  );
}

function WrapupNoConditions(props){
  const firstName = props.firstName;
  return(
    <>
      <p>Based on your responses so far, you haven't observed notable visual issues with {firstName} at this point.</p>
    </>
  );
}

//content sub-component with unique content for this page
function EvalAbilitiesVisualWrapupContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visionIssues = props.visionIssues;

  //TODO: combined condition for whether there are ANY considerations to keep in mind
  return(
    <>
    {anyVisualConditions(wearsGlasses, betterWithGlasses, hasOtherVisualAids, visionIssues) ?
    <WrapupConditions 
      firstName={firstName} pronouns={pronouns}
      wearsGlasses={wearsGlasses} betterWithGlasses={betterWithGlasses} hasOtherVisualAids={hasOtherVisualAids} visionIssues={visionIssues} />
    : <WrapupNoConditions firstName={firstName}/>}
    <p><strong>Keep in mind</strong> that you may learn more about vision issues during device test-drives -- so keep paying attention.</p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function EvaluateAbilitiesVisualWrapup(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visionIssues = props.visionIssues;
  const onContinue = props.onContinue;

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-eye",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Some friendly advice about ${firstName}'s visual abilities`,
    subTitle: "",
    learnMore: {linkText: "Learn More about vision and alternative access",
                title: "Vision and Alternative Access",
                content: VISUAL_WRAPUP_HELP
                },
    nextRoute: "", //this is governed by onContinue
    sidebar: false
  }

  console.log("Rendering EvaluateAbilitiesVisualWrapup");
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <EvalAbilitiesVisualWrapupContent 
          firstName={firstName} 
          pronouns={pronouns}
          wearsGlasses={wearsGlasses}
          betterWithGlasses={betterWithGlasses}
          hasOtherVisualAids={hasOtherVisualAids}
          visionIssues={visionIssues}
        />}
      onContinue={onContinue} 
    />
    </>
  );
}

EvaluateAbilitiesVisualWrapup.propTypes = propTypes;
EvaluateAbilitiesVisualWrapup.defaultProps = defaultProps;

export default EvaluateAbilitiesVisualWrapup;